import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { basisFromCase, isSecondaryOnly, DATA_BASIS_LABEL, DATA_BASIS_TITLE, type DataBasis } from '../utils/dataBasis';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import API from '../services/api';
import { formatCaseDate } from '../utils/formatDateValue';
import {
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
  MapPinIcon,
  LockClosedIcon,
  XMarkIcon,
  ArrowsRightLeftIcon,
  ArchiveBoxArrowDownIcon,
  ArrowUturnLeftIcon,
  CheckIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import MapView from '../components/MapView';
import ActivityCard from '../components/ActivityCard';
import AiValidationPanel, { ValidationSummary, AiValidationPanelHandle, ANCHOR_LABELS } from '../components/AiValidationPanel';
import AiFieldMark from '../components/AiFieldMark';
import DeduplicationPanel, { DedupRun } from '../components/DeduplicationPanel';
import MergedIntoBanner from '../components/MergedIntoBanner';
import MarkDuplicateDrawer from '../components/MarkDuplicateDrawer';
import SplitButton, { SplitAction } from '../components/SplitButton';
import CollapsibleCard from '../components/CollapsibleCard';
import TagInput from '../components/TagInput';
import SectionNav, { NavSection } from '../components/SectionNav';
import { scrollToSection } from '../utils/scrollToSection';
import { jumpToField, JumpOrigin } from '../utils/fieldAnchors';
import { computeGaps } from '../utils/documentationGaps';
import { yesNo, yesNoClass, isEndState } from '../utils/yesNo';
import ViolationDetailCard from '../components/ViolationDetailCard';
import VictimOutcomesTable, { OutcomeTableRow } from '../components/VictimOutcomesTable';
import { DefGrid, Def } from '../components/DefList';
import NotDocumented from '../components/NotDocumented';
import EvidenceCard from '../components/EvidenceCard';
import MarkdownText from '../components/MarkdownText';
import { SuggestionsProvider, SuggestionsCard } from '../components/CaseSuggestions';
import { describeValue, defineValue } from '../types/taxonomy';
import { getViolationColor, VICTIM_GROUP_TYPES, COMMUNITY_ROLE_TYPES, ACCUSATION_OUTCOMES, DETENTION_TYPES, DUE_PROCESS_VIOLATIONS, IMPACT_TYPES, REJECTION_GROUNDS, PERPETRATOR_TYPES, SUGGESTED_CASE_TAGS } from '../types/caseForm';
import { enumLabel } from '../utils/enumLabel';

const optionLabel = (options: { value: string; label: string }[], value: string) =>
  options.find(o => o.value === value)?.label || value;

// Workflow status chip styling and label, shared by the header and the sidebar. Colours follow
// the Figma "Case Status" set: grey draft, amber validation, orange research, sky approval, green
// approved, red rejected.
const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  FOR_VALIDATION: 'bg-amber-200 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  IN_RESEARCH: 'bg-orange-200 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200',
  FOR_APPROVAL: 'bg-sky-200 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
  APPROVED: 'bg-emerald-200 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  REJECTED: 'bg-red-200 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  ARCHIVED: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};
// Header chips (Figma "Case Origin / Status / Access / Violation"): 30px tall, 4px radius.
const CHIP = 'inline-flex h-[30px] items-center rounded px-3 text-xs font-medium whitespace-nowrap';
const ORIGIN_CHIP: Record<string, { label: string; className: string }> = {
  PRIMARY: { label: 'Primary', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200' },
  SECONDARY: { label: 'Secondary', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200' },
  MIXED: { label: 'Mixed', className: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200' },
};
// The header shows this many violation chips before folding the rest behind "+n more".
const VIOLATION_CHIPS_SHOWN = 3;
const statusStyle = (status: string) => STATUS_STYLES[status] || STATUS_STYLES.DRAFT;

const statusLabel = (status: string) =>
  status === 'FOR_VALIDATION' ? 'For Validation'
  : status === 'IN_RESEARCH' ? 'In Research'
  : status === 'FOR_APPROVAL' ? 'For Approval'
  : status.charAt(0) + status.slice(1).toLowerCase();

// Per-person outcome summary of a victim's CRIMINALIZATION or ARBITRARY_DETENTION row.
type OutcomeRow = { violationType?: string; otherViolationType?: string | null; accusationOutcome?: string | null; detentionType?: string | null; detentionDays?: number | null; dueProcessViolations?: string[] };
// The victim rows that can carry per-person outcomes, with a display label each.
const outcomeRows = (rows: OutcomeRow[] | undefined): Array<[string, OutcomeRow]> => {
  const list = Array.isArray(rows) ? rows : [];
  const out: Array<[string, OutcomeRow]> = [];
  const crim = list.find(t => t.violationType === 'CRIMINALIZATION');
  if (crim) out.push(['Criminalization', crim]);
  const ad = list.find(t => t.violationType === 'OTHER' && t.otherViolationType === 'ARBITRARY_DETENTION');
  if (ad) out.push(['Arbitrary detention', ad]);
  return out;
};
const CrimOutcomeSummary: React.FC<{ row?: OutcomeRow; label?: string }> = ({ row, label }) => {
  if (!row) return null;
  const dpv = row.dueProcessViolations || [];
  if (!row.accusationOutcome && !row.detentionType && row.detentionDays == null && dpv.length === 0) return null;
  return (
    <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 space-y-1">
      {label && <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>}
      {row.accusationOutcome && (
        <div><span className="font-semibold">Outcome of Accusations:</span> {optionLabel(ACCUSATION_OUTCOMES, row.accusationOutcome)}</div>
      )}
      {(row.detentionType || row.detentionDays != null) && (
        <div>
          <span className="font-semibold">Detention:</span>
          {row.detentionType ? ` ${optionLabel(DETENTION_TYPES, row.detentionType)}` : ''}
          {row.detentionDays != null ? ` · ${row.detentionDays === 0 ? 'less than a day' : `${row.detentionDays} ${row.detentionDays === 1 ? 'day' : 'days'}`}` : ''}
        </div>
      )}
      {dpv.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="font-semibold mr-1">Due-process violations:</span>
          {dpv.map(v => (<span key={v} className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">{optionLabel(DUE_PROCESS_VIOLATIONS, v)}</span>))}
        </div>
      )}
    </div>
  );
};

interface DevelopmentProjectRow {
  id?: string;
  sortOrder?: number;
  type?: string | null;
  subtypes?: string[];
  name?: string | null;
  description?: string | null;
  startYear?: number | null;
  source?: string | null;
  websiteLink?: string | null;
}

interface CaseData {
  id: string;
  caseNumber: string;
  status: string;
  rejectionCategory?: string | null;
  rejectionReason?: string | null;
  // Set when this case was archived as a confirmed duplicate: the case it resolves to.
  mergedIntoCaseId?: string | null;
  mergedInto?: { id: string; caseNumber: string; status: string } | null;
  incidentDate: string;
  incidentTime?: string;
  reportedDate: string;
  description: string;
  violationTypes: string[];
  country: string;
  region?: string;
  city?: string;
  specificLocation?: string;
  latitude?: number;
  longitude?: number;
  indigenousGroup: string;
  indigenousTerritory?: string;
  priority: number;
  tags: string[];
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  submittedBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
  reviewedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    profilePicture?: string | null;
  };
  assignedAt?: string;

  // Development Project Information
  isRelatedToDevelopmentProject?: boolean | null;
  developmentProjectType?: string;
  developmentProjectName?: string;
  developmentProjectDescription?: string;
  developmentProjectStartYear?: number;
  developmentProjectSource?: string;
  developmentProjectWebsiteLink?: string;
  // The project rows (several per case; the first is the primary one). The six flat fields above
  // are the legacy mirror of the primary row.
  developmentProjects?: DevelopmentProjectRow[];

  // Case Timeline
  isCaseOngoing?: boolean;
  caseStartDate?: string;
  caseStartDatePrecision?: 'DAY' | 'MONTH' | 'YEAR';
  caseEndDate?: string;

  // Violation Details (per-violation-type data)
  violationDetails: Array<{
    id: string;
    violationType: string;
    perpetratorKnown: boolean;
    perpetratorTypes: string[];
    perpetratorDetails?: string;
    perpetratorAccountabilityStatus?: string;
    legalCaseFiled: boolean;
    legalCaseDetails?: string;
    legalCaseWhyNot?: string;
    stateActionUndertaken: boolean;
    stateActionDetails?: string;
    stateActionWhyNot?: string;
    criminalizationTypes?: string[];
    criminalizationTriggers?: string[];
    legalInstrumentTypes?: string[];
    proceedingType?: string;
    isRetaliatoryProceeding?: boolean;
    whyVictimsCriminalized?: string;
    harassmentPrior?: boolean;
    harassmentPriorDetails?: string;
    accusations?: string;
    policiesOrLawsUsed?: string;
    whoFiledAccusations?: string[];
    whoFiledAccusationsDetails?: string;
    accusationsLegallyFiled?: boolean;
    courtFilingLocation?: string;
    courtFilingDetails?: string;
    decisionOnAccusations?: boolean;
    decisionDetails?: string;
    victimsInPrison?: boolean;
    prisonConditions?: string;
    victimsSituationIfNotInPrison?: string;
    whyVictimsKilled?: string;
    howKillingCarriedOut?: string;
    cause?: string;
  }>;
  otherViolationTypes?: string[];
  otherViolationTypeDetails?: string;

  // Investigation and Advocacy (case-level)
  investigationConducted?: boolean | null;
  investigationDetails?: string;
  investigationWhyNot?: string;
  advocacyUndertaken?: boolean | null;
  advocacyDetails?: string;
  advocacyWhyNot?: string;

  // Impact
  familyImpact?: string;
  communityImpact?: string;
  impactTypes?: string[];

  // Source Information
  documentorName?: string;
  documentorOrganization?: string;
  documentorEmail?: string;
  documentorPhone?: string;
  isPrimaryData?: boolean;
  dataBasis?: DataBasis;
  isAutoSourced?: boolean;
  dataSource?: string;
  dataUsageRestriction?: string;
  willingToShareMoreInfo?: boolean;
  isLDSFApplicant?: boolean;
  ldsfPaymentCompletedDate?: string;
  sourceInformation?: string;
  metadata?: {
    additionalSources?: Array<{ url: string; platform?: string | null; kind?: string; addedAt?: string }>;
    ai?: {
      autoApplied?: Array<{ field: string; value: unknown; priorValue: unknown; origin?: string; source?: string | null; snippet?: string; appliedAt?: string; undone?: boolean }>;
      conflicts?: Array<{ id?: string; field?: string; currentValue: unknown; proposedValue: unknown; origin?: string; source?: string | null; snippet?: string; resolved?: boolean; resolution?: unknown }>;
    };
  };

  victims: Array<{
    id: string;
    isGroup: boolean;
    violationDetails?: Array<{ violationType: string; otherViolationType?: string | null; affectedCount?: number | null; affectedMaleCount?: number | null; affectedFemaleCount?: number | null; affected0to12Count?: number | null; affected13to25Count?: number | null; affected26to35Count?: number | null; affected36to59Count?: number | null; affected60PlusCount?: number | null; accusationOutcome?: string | null; detentionType?: string | null; detentionDays?: number | null; dueProcessViolations?: string[] }>;
    firstName?: string;
    lastName?: string;
    ageBracket?: string;
    gender?: string;
    dependentType?: string;
    dependentDetails?: string;
    communityRole?: string;
    communityRoleType?: string;
    indigenousGroupOfVictim?: string;
    isIndigenous?: boolean;
    dateOfBirth?: string;
    additionalInfo?: string;
    // Group victim fields
    groupName?: string[];
    groupSize?: number;
    groupType?: string;
    affectedIndividualsCount?: number;
    affectedFamiliesCount?: number;
    affectedHomesCount?: number;
    affectedGroupsCount?: number;
    affectedCommunitiesCount?: number;
    affectedVillagesCount?: number;
    affectedMaleCount?: number;
    affectedFemaleCount?: number;
    affected0to12Count?: number;
    affected13to25Count?: number;
    affected26to35Count?: number;
    affected36to59Count?: number;
    affected60PlusCount?: number;
  }>;
  perpetrators: Array<{
    id: string;
    isOrganization: boolean;
    name: string;
    type: string;
    affiliation?: string;
    notes?: string;
  }>;
}

const CaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [caseData, setCaseData] = useState<CaseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [editingCaseInfo, setEditingCaseInfo] = useState(false);
  const [editPriority, setEditPriority] = useState(0);
  const [editAssignee, setEditAssignee] = useState('');
  const [editReportedDate, setEditReportedDate] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: string; firstName: string; lastName: string; role: string }>>([]);
  const [submitErrors, setSubmitErrors] = useState<Array<{ field: string; message: string }>>([]);
  // Dedup summary lifted from DeduplicationPanel so we can show a page-level "possible duplicate" banner.
  const [dedupSummary, setDedupSummary] = useState<{ pending: number; confirmed: number; runs: DedupRun[] }>({ pending: 0, confirmed: 0, runs: [] });
  const handleDedupSummary = useCallback((s: { pending: number; confirmed: number; runs: DedupRun[] }) => setDedupSummary(s), []);
  // Validation summary lifted from AiValidationPanel for the header strip.
  const [validationSummary, setValidationSummary] = useState<ValidationSummary | null>(null);
  // Last real content edit (from the audit log via ActivityCard); drives the AI panel's stale-run notice.
  const [lastEditAt, setLastEditAt] = useState<string | null>(null);
  const handleLastEditAt = useCallback((iso: string | null) => setLastEditAt(iso), []);
  const [decidedAt, setDecidedAt] = useState<{ at: string; by: string | null } | null>(null);
  const handleDecidedAt = useCallback((d: { at: string; by: string | null } | null) => setDecidedAt(d), []);
  const handleValidationSummary = useCallback((s: ValidationSummary) => setValidationSummary(s), []);
  // Sticky header height, so section jumps land below it. 0 when the header isn't sticky (small screens).
  const headerRef = useRef<HTMLDivElement>(null);
  // Lets the readiness links open the AI panel's drawers directly.
  const aiPanelRef = useRef<AiValidationPanelHandle>(null);
  // The header keeps the full summary at the top of the page and collapses to a two-row bar once
  // the reader scrolls into the case (large screens only, where it is sticky). Hysteresis avoids
  // flicker; the layout effect offsets the height change so the content under the bar stays put.
  const [condensed, setCondensed] = useState(false);
  const [showAllViolations, setShowAllViolations] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const el = headerRef.current;
      if (!el || window.getComputedStyle(el).position !== 'sticky') { setCondensed(false); return; }
      const y = window.scrollY;
      setCondensed((c) => (c ? y > 24 : y > 220));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, []);
  const prevHeaderHeight = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const h = el.offsetHeight;
    if (prevHeaderHeight.current != null && condensed && h < prevHeaderHeight.current) {
      window.scrollBy(0, h - prevHeaderHeight.current);
    }
    prevHeaderHeight.current = h;
  }, [condensed]);
  // Trends-only cases: roles below Approver see victim identifiers and documenter contact details
  // masked until they choose to reveal them (screen-level guard; the PDF export masks for everyone).
  const [revealIdentifiers, setRevealIdentifiers] = useState(false);

  // Manual merge: the header menu opens the picker in one of two
  // directions; the picker creates a MANUAL match and the merge page (/merge/:matchId) takes over.
  const [markMode, setMarkMode] = useState<'duplicate' | 'canonical' | null>(null);
  // After a jump from the AI or suggestions drawer to a field: where to go back to.
  const [reviewReturn, setReviewReturn] = useState<{ origin: JumpOrigin; scrollY: number } | null>(null);
  const [suggestionsOpenRequests, setSuggestionsOpenRequests] = useState(0);
  // The map is on request: coordinates are shown as text, the map only when asked for.
  const [showMap, setShowMap] = useState(false);
  const getHeaderOffset = useCallback(() => {
    const el = headerRef.current;
    if (!el) return 0;
    return window.getComputedStyle(el).position === 'sticky' ? el.offsetHeight : 0;
  }, []);

  useEffect(() => {
    if (id) {
      fetchCase();
      fetchDocuments();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCase = async () => {
    try {
      const response = await API.get(`/api/cases/${id}`);
      setCaseData(response.data.case);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch case');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!id) return;

    setDocumentsLoading(true);
    try {
      const response = await API.get(`/api/documents/case/${id}`);
      setDocuments(response.data.documents || []);
    } catch (err: any) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await API.get('/api/users');
      const users = response.data.users || [];
      setAvailableUsers(users.filter((u: any) => ['RESEARCHER', 'APPROVER', 'ADMIN'].includes(u.role)));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleAssignment = async (assignedToId: string | null) => {
    setUpdating(true);
    try {
      await API.put(`/api/cases/${id}/assign`, { assignedToId });
      await fetchCase();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to assign case');
    } finally {
      setUpdating(false);
    }
  };

  const downloadDocument = async (documentId: string) => {
    try {
      const response = await API.get(`/api/documents/${documentId}/download`);
      window.open(response.data.downloadUrl, '_blank');
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to get download URL');
    }
  };

  // withIdentifiers: the internal copy of a trends-only case (Approver/Admin only, server-checked).
  const exportToPDF = async (withIdentifiers = false) => {
    try {
      const response = await API.get(`/api/export/cases/${id}/pdf${withIdentifiers ? '?identifiers=1' : ''}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `case-${caseData?.caseNumber || id}${withIdentifiers ? '-INTERNAL-identifiers' : ''}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Export PDF failed:', err);
      showToast('error', err.response?.data?.message || 'Failed to export case to PDF');
    }
  };

  // Reject flow: a required structured ground + free-text note, captured on the case.
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectCategory, setRejectCategory] = useState('');
  const [rejectNote, setRejectNote] = useState('');

  // Return-for-research flow: a required note, saved as an internal comment so the researcher
  // sees why the case came back. The backend has no field for it; comments are the handoff channel.
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnNote, setReturnNote] = useState('');
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const returnForResearch = async () => {
    if (!caseData || !returnNote.trim()) return;
    setUpdating(true);
    try {
      await API.post(`/api/cases/${id}/comments`, {
        content: `Returned for research: ${returnNote.trim()}`,
        isInternal: true,
      });
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to save the note. The case was not returned.');
      setUpdating(false);
      return;
    }
    setCommentsRefreshKey((k) => k + 1);
    try {
      const response = await API.put(`/api/cases/${id}`, { status: 'IN_RESEARCH' });
      setCaseData(response.data.case);
      setReturnOpen(false);
      setReturnNote('');
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'The note was saved but the case could not be returned.');
    } finally {
      setUpdating(false);
    }
  };
  const rejectCase = async () => {
    if (!caseData || !rejectNote.trim()) return;
    setUpdating(true);
    try {
      const response = await API.put(`/api/cases/${id}`, {
        status: 'REJECTED',
        rejectionCategory: rejectCategory || null,
        rejectionReason: rejectNote.trim(),
      });
      setCaseData(response.data.case);
      setRejectOpen(false);
      setRejectCategory('');
      setRejectNote('');
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to reject case');
    } finally {
      setUpdating(false);
    }
  };

  const updateCaseStatus = async (newStatus: string) => {
    if (!caseData) return;

    setUpdating(true);
    try {
      const response = await API.put(`/api/cases/${id}`, {
        status: newStatus
      });
      setCaseData(response.data.case);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        showToast('error', err.response.data.errors.map((e: any) => e.message || e.msg || `${e.field || e.path}: validation failed`));
      } else {
        showToast('error', err.response?.data?.message || 'Failed to update case status');
      }
    } finally {
      setUpdating(false);
    }
  };

  const submitCase = async () => {
    if (!caseData) return;

    setSubmitErrors([]);
    setUpdating(true);
    try {
      const response = await API.put(`/api/cases/${id}`, {
        status: 'FOR_VALIDATION'
      });
      setCaseData(response.data.case);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        setSubmitErrors(
          err.response.data.errors.map((e: any) => ({
            field: e.field || e.path || e.param || '',
            message: e.message || e.msg || ''
          }))
        );
      } else {
        setSubmitErrors([{
          field: '',
          message: err.response?.data?.message || 'Failed to submit case.'
        }]);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setUpdating(false);
    }
  };

  const updateCasePriority = async (newPriority: number) => {
    if (!caseData) return;

    setUpdating(true);
    try {
      const response = await API.put(`/api/cases/${id}`, {
        priority: newPriority
      });
      setCaseData(response.data.case);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        showToast('error', err.response.data.errors.map((e: any) => e.message || e.msg || `${e.field || e.path}: validation failed`));
      } else {
        showToast('error', err.response?.data?.message || 'Failed to update case priority');
      }
    } finally {
      setUpdating(false);
    }
  };


  const deleteCase = async () => {
    const confirmed = await confirm({
      title: 'Delete Case',
      message: 'Are you sure you want to delete this case? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await API.delete(`/api/cases/${id}`);
      navigate('/cases');
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to delete case');
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 1: return 'Low';
      case 2: return 'Med-low';
      case 3: return 'Medium';
      case 4: return 'Med-high';
      case 5: return 'High';
      default: return 'Unknown';
    }
  };

  const getViolationTypeLabel = (type: string) => enumLabel(type);

  const canEdit = () => {
    if (!user || !caseData) return false;
    return user.role === 'ADMIN' ||
           user.role === 'APPROVER' ||
           (user.role === 'RESEARCHER' && caseData.status !== 'APPROVED' && caseData.status !== 'FOR_APPROVAL') ||
           (user.id === caseData.submittedBy.id && caseData.status === 'DRAFT');
  };

  const canDelete = () => {
    if (!user || !caseData) return false;
    return user.role === 'ADMIN' || 
           (user.id === caseData.submittedBy.id && caseData.status === 'DRAFT');
  };

  // Mirrors documentController.uploadDocuments' rule.
  const canUploadDocuments = () => {
    if (!user || !caseData) return false;
    return user.role === 'ADMIN' || user.role === 'APPROVER' ||
           (user.id === caseData.submittedBy.id && ['DRAFT', 'FOR_VALIDATION'].includes(caseData.status));
  };

  const canUpdateStatus = () => {
    if (!user || !caseData) return false;
    return user.role === 'ADMIN' || user.role === 'APPROVER' || user.role === 'RESEARCHER';
  };

  // What still needs a human before the case moves on. Unresolved AI source conflicts are the one
  // item the backend is certain to reject on Submit / Mark For Approval (caseController's
  // validateCaseReadyForSubmission); the rest are advisories the reviewer should look at first.
  // What each open item blocks. Submit / Mark For Approval are gated by conflicts only (the
  // server's submission check); Approve is gated by conflicts, open blockers and unchecked AI
  // fields (the server's approval check). Duplicates and missing anchors are advisories.
  type Gate = 'submit' | 'approve';
  type ReadinessItem = { key: string; label: string; blocks: Gate[]; target?: string; action?: () => void };
  const getReadiness = (): { items: ReadinessItem[]; blocksSubmit: boolean; blocksApprove: boolean } => {
    const items: ReadinessItem[] = [];
    if (!caseData) return { items, blocksSubmit: false, blocksApprove: false };
    const openConflicts = (caseData.metadata?.ai?.conflicts ?? []).filter((c) => !c.resolved).length;
    if (openConflicts > 0) {
      items.push({ key: 'conflicts', label: `${openConflicts} source conflict${openConflicts === 1 ? '' : 's'} to resolve`, blocks: ['submit', 'approve'], target: 'section-ai-validation', action: () => aiPanelRef.current?.openDrawer('resolution') });
    }
    // Proposed updates from merged duplicates block approval (mirrors caseController.approvalBlockers;
    // bare source adds do not count). The action opens the proposed-updates review drawer.
    const pendingMerges = (((caseData.metadata as any)?.ai?.pendingUpdates ?? []) as Array<{ status?: string; type?: string }>)
      .filter((u) => u && u.status === 'PENDING' && u.type !== 'source').length;
    if (pendingMerges > 0) {
      items.push({ key: 'pending-updates', label: `${pendingMerges} proposed update${pendingMerges === 1 ? '' : 's'} from merged cases to resolve`, blocks: ['approve'], action: () => setSuggestionsOpenRequests((n) => n + 1) });
    }
    if (dedupSummary.confirmed > 0) {
      items.push({ key: 'duplicate', label: 'confirmed duplicate', blocks: [], target: 'section-dedup' });
    } else if (dedupSummary.pending > 0) {
      items.push({ key: 'duplicate', label: `${dedupSummary.pending} possible duplicate${dedupSummary.pending === 1 ? '' : 's'} pending review`, blocks: [], target: 'section-dedup' });
    }
    if (validationSummary && validationSummary.blockers > 0) {
      items.push({ key: 'blockers', label: `${validationSummary.blockers} AI blocker${validationSummary.blockers === 1 ? '' : 's'} to resolve`, blocks: ['approve'], target: 'section-ai-validation', action: () => aiPanelRef.current?.openDrawer('blockers') });
    }
    const activeAi = (caseData.metadata?.ai?.autoApplied ?? []).filter((e) => !e.undone && !(e as any).superseded);
    const unconfirmedAi = activeAi.filter((e) => !(e as any).confirmedAt).length;
    if (unconfirmedAi > 0) {
      items.push({ key: 'unconfirmed', label: `${unconfirmedAi} AI-filled field${unconfirmedAi === 1 ? '' : 's'} not yet checked`, blocks: ['approve'], target: 'section-ai-validation', action: () => aiPanelRef.current?.openDrawer('autofilled') });
    }
    if (validationSummary && validationSummary.missingAnchors.length > 0) {
      const labels = validationSummary.missingAnchors.map((a) => (ANCHOR_LABELS[a] || a).toLowerCase());
      items.push({ key: 'anchors', label: `missing ${labels.join(', ')}`, blocks: [], target: 'section-ai-validation', action: () => aiPanelRef.current?.openCompleteness() });
    }
    // Informational AI notes: nothing to clear, but the reviewer should have read them.
    if (validationSummary && validationSummary.notes > 0) {
      items.push({ key: 'notes', label: `${validationSummary.notes} AI note${validationSummary.notes === 1 ? '' : 's'} to read`, blocks: [], target: 'section-ai-validation', action: () => aiPanelRef.current?.openDrawer('notes') });
    }
    return { items, blocksSubmit: items.some((i) => i.blocks.includes('submit')), blocksApprove: items.some((i) => i.blocks.includes('approve')) };
  };

  // Approve is gated server-side too (caseController.approvalBlockers); the button is disabled
  // with the reasons while anything blocks it.
  const approveCase = async () => { await updateCaseStatus('APPROVED'); };

  // The workflow actions open to this user on this case, as descriptors. The first 'advance' one
  // is the split button's primary action; the rest go in its menu.
  type StatusAction = SplitAction & { kind: 'advance' | 'return' | 'reject' };
  const getStatusActions = (): StatusAction[] => {
    if (!user || !caseData) return [];
    const readinessNow = getReadiness();
    const blocked = readinessNow.blocksSubmit;
    const blockedTitle = blocked ? 'Resolve the AI source conflicts first — the server will refuse this until they are resolved.' : undefined;
    const approveBlocked = readinessNow.blocksApprove;
    const approveTitle = approveBlocked
      ? `Cannot approve yet: ${readinessNow.items.filter((i) => i.blocks.includes('approve')).map((i) => i.label).join('; ')}.`
      : undefined;

    const role = user.role;
    const status = caseData.status;
    const isAdmin = role === 'ADMIN';
    const out: StatusAction[] = [];
    const openReject = () => { setRejectCategory(''); setRejectNote(''); setRejectOpen(true); };

    // DRAFT → FOR_VALIDATION (Documenter, Reviewer, Manager, Admin)
    if (status === 'DRAFT' && (isAdmin || role === 'APPROVER' || role === 'RESEARCHER' || user.id === caseData.submittedBy.id)) {
      out.push({ kind: 'advance', key: 'submit', label: 'Submit Case', icon: PaperAirplaneIcon, onClick: submitCase, disabled: updating || blocked, title: blockedTitle });
    }
    // FOR_VALIDATION → IN_RESEARCH (Researcher, Approver, Admin)
    if (status === 'FOR_VALIDATION' && (isAdmin || role === 'APPROVER' || role === 'RESEARCHER')) {
      out.push({ kind: 'advance', key: 'start-review', label: 'Start Review', icon: CheckIcon, onClick: () => updateCaseStatus('IN_RESEARCH'), disabled: updating });
    }
    // IN_RESEARCH → FOR_APPROVAL (Researcher, Approver, Admin)
    if (status === 'IN_RESEARCH' && (isAdmin || role === 'APPROVER' || role === 'RESEARCHER')) {
      out.push({ kind: 'advance', key: 'validate', label: 'Mark For Approval', icon: CheckIcon, onClick: () => updateCaseStatus('FOR_APPROVAL'), disabled: updating || blocked, title: blockedTitle });
    }
    // FOR_APPROVAL → APPROVED, REJECTED, or return to IN_RESEARCH (Approver, Admin)
    if (status === 'FOR_APPROVAL' && (isAdmin || role === 'APPROVER')) {
      out.push(
        { kind: 'advance', key: 'approve', label: 'Approve', icon: CheckIcon, onClick: approveCase, disabled: updating || approveBlocked, title: approveTitle },
        { kind: 'return', key: 'return-review', label: 'Return for Research', icon: ArrowUturnLeftIcon, onClick: () => { setReturnNote(''); setReturnOpen(true); }, disabled: updating },
        { kind: 'reject', key: 'reject', label: 'Reject', icon: XMarkIcon, danger: true, onClick: openReject, disabled: updating },
      );
    }
    // Reject directly from review — an APPROVER/ADMIN can reject without first advancing the case
    // to the approval stage. (The approval-stage Reject above covers FOR_APPROVAL.)
    if ((status === 'FOR_VALIDATION' || status === 'IN_RESEARCH') && (isAdmin || role === 'APPROVER')) {
      out.push({ kind: 'reject', key: 'reject-early', label: 'Reject', icon: XMarkIcon, danger: true, onClick: openReject, disabled: updating });
    }
    return out;
  };

  // One split button for the header: the next workflow step first; everything else in the menu.
  const renderHeaderActions = () => {
    if (!caseData) return null;
    const statusActions = getStatusActions();
    const advance = statusActions.find((a) => a.kind === 'advance');
    const secondaryStatus = statusActions.filter((a) => a !== advance);
    const canMerge = (user?.role === 'ADMIN' || user?.role === 'APPROVER' || user?.role === 'RESEARCHER') && caseData.status !== 'ARCHIVED' && caseData.status !== 'REJECTED';
    const canInternalPdf = isTrendsOnly && (user?.role === 'ADMIN' || user?.role === 'APPROVER');

    const edit: SplitAction | null = canEdit() ? { key: 'edit', label: 'Edit', icon: PencilIcon, onClick: () => navigate(`/cases/${id}/edit`) } : null;
    const exportStd: SplitAction = { key: 'pdf', label: 'Export PDF', icon: ArrowDownTrayIcon, onClick: () => exportToPDF(false), hint: isTrendsOnly ? 'Victim identifiers omitted on this restricted case' : undefined, title: isTrendsOnly ? 'Export case to PDF (victim identifiers are omitted on this restricted case)' : 'Export case to PDF' };
    const exportInternal: SplitAction | null = canInternalPdf ? { key: 'pdf-internal', label: 'Export internal copy with identifiers', icon: LockClosedIcon, onClick: () => exportToPDF(true), hint: 'Includes victim names and dates of birth. Marked restricted on every page. IPRI use only.' } : null;
    const markDup: SplitAction | null = canMerge ? { key: 'mark-dup', label: 'Mark as duplicate of…', icon: ArchiveBoxArrowDownIcon, onClick: () => setMarkMode('duplicate'), hint: 'A second write-up of an event already on record; archived into that case.' } : null;
    const mergeInto: SplitAction | null = canMerge ? { key: 'merge-into', label: 'Merge another case into this one', icon: ArrowsRightLeftIcon, onClick: () => setMarkMode('canonical'), hint: 'Another record describes this same event; archived into this case.' } : null;
    const del: SplitAction | null = canDelete() ? { key: 'delete', label: 'Delete', icon: TrashIcon, danger: true, onClick: deleteCase } : null;

    // Primary: the workflow step, else Edit, else Export.
    const primary = advance ?? edit ?? exportStd;
    const rest: Array<SplitAction | 'divider'> = [];
    const push = (...xs: Array<SplitAction | null>) => { for (const x of xs) if (x && x !== primary) rest.push(x); };
    push(...secondaryStatus);
    if (rest.length) rest.push('divider');
    const before = rest.length;
    push(edit, exportStd, exportInternal);
    if (rest.length > before && (markDup || mergeInto)) rest.push('divider');
    push(markDup, mergeInto);
    if (del) { if (rest.length) rest.push('divider'); rest.push(del); }
    // No divider at the very start or end.
    while (rest[0] === 'divider') rest.shift();
    while (rest[rest.length - 1] === 'divider') rest.pop();

    return <SplitButton primary={primary} items={rest} tone={advance ? 'primary' : 'neutral'} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-flex items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mr-3"></div>
          Loading case details...
        </div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">Error loading case</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error || 'Case not found'}</p>
        <div className="mt-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700"
          >
            <ChevronLeftIcon className="h-5 w-5 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Inline AI marker for a given field path — renders only if the AI auto-filled that field.
  // Track which fields got an inline AI mark this render, so the catch-all below can surface
  // any auto-filled field that isn't shown inline — guaranteeing none is ever invisible.
  const markedFields = new Set<string>();
  const humanizeField = (f: string): string => {
    const last = (f.split('.').pop() || f).replace(/\[.*?\]/g, '');
    return last.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
  };
  const aiMark = (field: string | string[]) => {
    (Array.isArray(field) ? field : [field]).forEach((f) => markedFields.add(f));
    return <AiFieldMark field={field} caseId={id!} ai={caseData.metadata?.ai} onChange={fetchCase} />;
  };
  // Renders inline marks for every auto-filled field NOT already marked above. Place at the bottom.
  const aiLeftovers = () => {
    const entries = ((caseData.metadata?.ai?.autoApplied as any[]) ?? []).filter(
      (e) => !e.undone && !e.superseded && e.field && !markedFields.has(e.field)
    );
    if (!entries.length) return null;
    // One badge per unique field — AiFieldMark already aggregates every entry for a field into a
    // single badge/popup, so mapping over raw entries would render duplicate badges (e.g. several
    // "Victims" adds each showing the same list). De-dupe to distinct fields, preserving order.
    const fields = Array.from(new Set(entries.map((e) => e.field)));
    return (
      <div className="mt-4 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Other fields auto-filled by AI</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {fields.map((f) => (
            <span key={f} className="text-xs text-gray-700 dark:text-gray-300 inline-flex items-center gap-0.5">
              <span className="font-medium">{humanizeField(f)}:</span>{aiMark(f)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const isTrendsOnly = caseData.dataUsageRestriction === 'IPRI_ONLY_TRENDS';
  const maskIdentifiers = isTrendsOnly && !(user?.role === 'ADMIN' || user?.role === 'APPROVER');
  const hideIds = maskIdentifiers && !revealIdentifiers;

  // Per-victim criminalization / arbitrary-detention outcomes, for the table in the
  // Criminalization card. Names follow the same masking rule as the victim cards.
  const namedVictims = caseData.victims.filter((v) => !v.isGroup);
  const crimOutcomeRows: OutcomeTableRow[] = caseData.victims.flatMap((v) => {
    const namedIdx = namedVictims.indexOf(v);
    const label = v.isGroup
      ? (Array.isArray(v.groupName) && v.groupName.length ? `Group: ${v.groupName.join(', ')}` : 'Group victim')
      : hideIds ? `Victim ${namedIdx + 1}` : ([v.firstName, v.lastName].filter(Boolean).join(' ') || `Victim ${namedIdx + 1}`);
    return (v.violationDetails || [])
      .filter((t) => t.violationType === 'CRIMINALIZATION' || (t.violationType === 'OTHER' && t.otherViolationType === 'ARBITRARY_DETENTION'))
      .map((t) => ({
        victimId: v.id,
        label,
        isGroup: v.isGroup,
        affectedCount: t.affectedCount ?? v.affectedIndividualsCount ?? null,
        violation: (t.violationType === 'CRIMINALIZATION' ? 'Criminalization' : 'Arbitrary detention') as OutcomeTableRow['violation'],
        accusationOutcome: t.accusationOutcome,
        detentionType: t.detentionType,
        detentionDays: t.detentionDays,
        dueProcessViolations: t.dueProcessViolations,
      }));
  });

  // Expected fields not yet documented, shown in place. Comes from the last validation run when
  // there is one (re-checked against current data), else a baseline subset.
  const gaps = computeGaps(caseData as Record<string, any>, validationSummary?.missing ?? null);
  const gapsEditable = canEdit();

  // Which optional sections render — used both by the cards below and by the section nav.
  const crimDetail = caseData.violationDetails?.find(vd => vd.violationType === 'CRIMINALIZATION');
  const killDetail = caseData.violationDetails?.find(vd => vd.violationType === 'KILLING');
  const otherDetail = caseData.violationDetails?.find(vd => vd.violationType === 'OTHER');
  const hasOtherData = Boolean(otherDetail ||
    (caseData.otherViolationTypes && caseData.otherViolationTypes.length > 0) ||
    caseData.otherViolationTypeDetails);
  const showPerpetrators = Boolean(caseData.perpetrators && caseData.perpetrators.length > 0);
  const showImpact = Boolean((caseData.familyImpact || caseData.communityImpact || (caseData.impactTypes && caseData.impactTypes.length > 0))) || gaps.impact.length > 0;
  // Project rows; a record saved before the project table existed still shows its legacy columns.
  const projectRows: DevelopmentProjectRow[] = (caseData.developmentProjects && caseData.developmentProjects.length > 0)
    ? caseData.developmentProjects
    : ((caseData.developmentProjectName || caseData.developmentProjectType || caseData.developmentProjectDescription || caseData.developmentProjectStartYear || caseData.developmentProjectSource)
      ? [{ type: caseData.developmentProjectType, subtypes: [], name: caseData.developmentProjectName, description: caseData.developmentProjectDescription, startYear: caseData.developmentProjectStartYear, source: caseData.developmentProjectSource, websiteLink: caseData.developmentProjectWebsiteLink }]
      : []);
  const showDevelopmentProject = Boolean(caseData.isRelatedToDevelopmentProject) || projectRows.length > 0;
  const showInvestigations = Boolean(caseData.investigationConducted !== undefined);
  const showAdvocacy = Boolean((caseData.advocacyDetails || caseData.advocacyUndertaken !== undefined));
  const showDataSource = Boolean((caseData.isAutoSourced || caseData.isPrimaryData !== undefined || caseData.documentorName || caseData.documentorPhone || caseData.dataSource || caseData.dataUsageRestriction));
  const readiness = getReadiness();
  const aiFieldEntries = (caseData.metadata?.ai?.autoApplied ?? []).filter((e) => !e.undone && !(e as any).superseded);
  const aiFieldTotal = aiFieldEntries.length;
  // Scroll to the section that shows an AI field path (from the AI drawer or the suggestions drawer).
  const handleJumpToField = (path: string, origin?: JumpOrigin) => {
    if (origin) setReviewReturn({ origin, scrollY: window.scrollY });
    jumpToField(path, caseData, getHeaderOffset());
  };
  const returnToReview = () => {
    if (!reviewReturn) return;
    window.scrollTo({ top: reviewReturn.scrollY, behavior: 'auto' });
    if (reviewReturn.origin.kind === 'ai') aiPanelRef.current?.openDrawer(reviewReturn.origin.tab as any);
    else setSuggestionsOpenRequests((n) => n + 1);
    setReviewReturn(null);
  };
  const showReadiness = readiness.items.length > 0 && !isEndState(caseData.status);
  // AI Assisted Verification is for bot-produced (auto-sourced) secondary-data cases only, visible
  // to Researchers (read-only), Approvers and Admins; AI actions stay Approver/Admin.
  const canSeeAiPanel = (user?.role === 'ADMIN' || user?.role === 'APPROVER' || user?.role === 'RESEARCHER') && isSecondaryOnly(caseData) && caseData.isAutoSourced === true;
  // The AI card is a review surface; once the case is decided the verification moves into Case
  // Information as part of the record.
  const showAiCard = Boolean(caseData.isAutoSourced || validationSummary?.isValidating || validationSummary?.decision || aiFieldTotal > 0) && !isEndState(caseData.status);
  const readinessBlocking = readiness.blocksApprove || readiness.blocksSubmit;
  // The open items as a heading plus a list. Shared by the AI card (stacked, inherits the card's
  // text colour) and the stand-alone banner shown when there is no AI card to hold them.
  const readinessList = (inCard: boolean) => (
    <>
      <p className={`font-semibold ${inCard ? (readinessBlocking ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200') : ''}`}>
        {readiness.blocksApprove
          ? 'Needs attention before this case can be approved'
          : readiness.blocksSubmit
          ? 'Needs attention before this case can be submitted'
          : 'Needs attention'}
      </p>
      <ul className={inCard ? 'mt-1.5 space-y-0.5 text-xs leading-5' : 'mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5'}>
        {readiness.items.map((it) => (
          <li key={it.key}>
            <button
              type="button"
              onClick={() => {
                // Inside the card, items that point at the card itself just open their drawer.
                if (it.target && !(inCard && it.target === 'section-ai-validation')) scrollToSection(it.target, getHeaderOffset(), { flash: true });
                it.action?.();
              }}
              className={`inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 text-left ${inCard ? 'font-medium text-blue-700 dark:text-blue-300 hover:underline' : 'underline decoration-dotted underline-offset-2 hover:decoration-solid'}`}
            >
              {it.label}
              {/* In the card the heading already says what is blocked; the tag is banner-only. */}
              {!inCard && it.blocks.length > 0 && (
                <span className="rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap bg-red-600 text-white no-underline">
                  {it.blocks.includes('submit') ? 'blocks submission' : 'blocks approval'}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </>
  );

  const navSections: NavSection[] = [
    { id: 'section-incident', label: 'Incident' },
    { id: 'section-location', label: 'Location' },
    { id: 'section-indigenous-group', label: 'Indigenous Group' },
    { id: 'section-victims', label: 'Victims' },
    ...(crimDetail ? [{ id: 'section-criminalization', label: 'Criminalization' }] : []),
    ...(killDetail ? [{ id: 'section-killing', label: 'Killing' }] : []),
    ...(hasOtherData ? [{ id: 'section-other-violations', label: 'Other Violations' }] : []),
    ...(showPerpetrators ? [{ id: 'section-perpetrators', label: 'Perpetrators' }] : []),
    ...(showImpact ? [{ id: 'section-impact', label: 'Impact' }] : []),
    ...(showDevelopmentProject ? [{ id: 'section-development-project', label: 'Development Project' }] : []),
    ...(showInvestigations ? [{ id: 'section-investigations', label: 'Investigations' }] : []),
    ...(showAdvocacy ? [{ id: 'section-advocacy', label: 'Advocacy' }] : []),
    { id: 'section-evidence', label: 'Sources & Documents' },
    ...(showDataSource ? [{ id: 'section-data-source', label: 'Documenter' }] : []),
  ];

  return (
    <SuggestionsProvider caseId={id!} caseData={caseData as Record<string, any>} onApplied={fetchCase} onJumpToField={handleJumpToField} openRequests={suggestionsOpenRequests}>
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Sticky header (Figma "Header w/ Navigation"): a white card with the identity row — case
          number, origin / status / access chips, the first violation chips with "+n more", and the
          primary action — then the facts row; the section pills sit under the card. On a restricted
          case the data-sharing notice is the card's top band. Collapses to the identity row once
          the reader scrolls (see `condensed`). */}
      {(() => {
        const basis = basisFromCase(caseData);
        const allViolations = [...caseData.violationTypes, ...(caseData.otherViolationTypes || [])];
        const visibleViolations = showAllViolations ? allViolations : allViolations.slice(0, VIOLATION_CHIPS_SHOWN);
        const hiddenViolations = allViolations.length - visibleViolations.length;
        const place = [caseData.region, caseData.country].filter(Boolean).join(', ');
        const ongoing = caseData.isCaseOngoing == null ? null : (caseData.isCaseOngoing ? 'Ongoing' : 'Concluded');
        return (
      <div ref={headerRef} className="lg:sticky lg:top-0 lg:z-20 -mx-4 -mt-8 px-4 pt-4 mb-2 bg-gray-50 dark:bg-gray-900">
        <div className="rounded-[10px] border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          {isTrendsOnly && (
            <div className="flex items-center gap-4 rounded-t-[10px] bg-orange-100 dark:bg-orange-900/30 px-3.5 py-3 text-sm leading-5 text-orange-700 dark:text-orange-200" role="note">
              <LockClosedIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                <p className="font-bold">Restricted</p>
                {!condensed && (
                  <p>Under the data sharing agreement, IPRI may use this case for aggregate trend analysis only. Do not publish, quote or share its details outside IPRI. The PDF export omits victim identifiers.</p>
                )}
                {!condensed && maskIdentifiers && (
                  <p className="flex flex-wrap items-center gap-2">
                    <span>{hideIds ? 'Victim names, dates of birth and documenter contact details are hidden for your role.' : 'Identifiers are shown.'}</span>
                    <button
                      type="button"
                      onClick={() => setRevealIdentifiers((v) => !v)}
                      className="inline-flex items-center px-2 py-0.5 rounded-md border border-orange-300 dark:border-orange-700 text-xs font-medium hover:bg-orange-200/60 dark:hover:bg-orange-900/50"
                    >
                      {hideIds ? 'Show identifiers' : 'Hide identifiers'}
                    </button>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Identity and review */}
          <div className={`flex items-center gap-2 px-4 py-4 ${condensed ? '' : 'border-b border-gray-100 dark:border-gray-700'}`}>
            <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-6 gap-y-2">
              <h1 className="text-[26px] leading-[34px] font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">{caseData.caseNumber}</h1>
              <div className="min-w-0 flex-1 flex flex-wrap items-center gap-2">
                {basis && ORIGIN_CHIP[basis] && (
                  <span className={`${CHIP} ${ORIGIN_CHIP[basis].className}`} title={DATA_BASIS_TITLE[basis]} aria-label={DATA_BASIS_LABEL[basis]}>{ORIGIN_CHIP[basis].label}</span>
                )}
                <span className={`${CHIP} ${statusStyle(caseData.status)}`}>{statusLabel(caseData.status)}</span>
                {caseData.status === 'REJECTED' && caseData.rejectionCategory && (
                  <span className={`${CHIP} border border-red-300 text-red-700 dark:border-red-700 dark:text-red-300`} title={caseData.rejectionReason || undefined}>
                    {caseData.rejectionCategory}
                  </span>
                )}
                {isTrendsOnly && (
                  <span className={`${CHIP} bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200`} title="Data sharing agreement: IPRI may use this case for trend analysis only. Do not publish case details.">
                    Trends Only
                  </span>
                )}
                {dedupSummary.confirmed > 0 ? (
                  <span className={`${CHIP} gap-1 bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300`} title="This case has been confirmed as a duplicate.">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Confirmed duplicate
                  </span>
                ) : dedupSummary.pending > 0 ? (
                  <span className={`${CHIP} gap-1 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`} title="Possible duplicate — check the Case Duplicate Checker before validating.">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Possible duplicate · {dedupSummary.pending} pending
                  </span>
                ) : null}
                {/* Violation chips fold away once condensed, so the sticky strip stays one line. */}
                {!condensed && (<>
                {visibleViolations.map((type, i) => (
                  <span key={`${type}-${i}`} title={describeValue('violationTypes', type)} className={`${CHIP} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200`}>
                    {getViolationTypeLabel(type)}
                  </span>
                ))}
                {hiddenViolations > 0 && (
                  <button type="button" onClick={() => setShowAllViolations(true)} className="text-[13px] font-medium text-blue-700 dark:text-blue-300 hover:underline whitespace-nowrap">
                    +{hiddenViolations} more
                  </button>
                )}
                {showAllViolations && allViolations.length > VIOLATION_CHIPS_SHOWN && (
                  <button type="button" onClick={() => setShowAllViolations(false)} className="text-[13px] font-medium text-blue-700 dark:text-blue-300 hover:underline whitespace-nowrap">
                    Show less
                  </button>
                )}
                {aiMark(['violationTypes', 'otherViolationTypes', 'violationDetails'])}
                </>)}
              </div>
            </div>
            <div className="flex justify-end shrink-0">
              {renderHeaderActions()}
            </div>
          </div>

          {/* Supporting case information — hidden once condensed. */}
          {!condensed && (
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 px-4 py-5 text-sm leading-5">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                <span title="Start date">
                  <span className="text-gray-500 dark:text-gray-400">Date : </span>
                  <span className="font-medium text-gray-700 dark:text-gray-200">{caseData.caseStartDate ? formatCaseDate(caseData.caseStartDate, caseData.caseStartDatePrecision) : 'No date'}</span>
                </span>
                {ongoing && (
                  <span title="Is the case ongoing?">
                    <span className="text-gray-500 dark:text-gray-400">Status : </span>
                    <span className="font-medium text-gray-700 dark:text-gray-200">{ongoing}</span>
                  </span>
                )}
              </div>
              <span title="Location">
                <span className="text-gray-500 dark:text-gray-400">Location : </span>
                <span className="font-medium text-gray-700 dark:text-gray-200">{place || 'Not recorded'}</span>
              </span>
            </div>
          )}
        </div>
        <div className="mt-2">
          <SectionNav sections={navSections} getOffset={getHeaderOffset} />
        </div>
      </div>
        );
      })()}

      {/* Merged-into: an archived duplicate says which case it resolves to, with Un-merge for
          APPROVER/ADMIN. First thing under the header so nobody reviews a record that no longer counts. */}
      {caseData.status === 'ARCHIVED' && (
        <MergedIntoBanner
          caseId={id!}
          caseNumber={caseData.caseNumber}
          mergedInto={caseData.mergedInto ?? null}
          merge={(caseData.metadata as any)?.merge ?? null}
          canUnmerge={user?.role === 'ADMIN' || user?.role === 'APPROVER' || user?.role === 'RESEARCHER'}
          onUnmerged={fetchCase}
        />
      )}

      {markMode && (
        <MarkDuplicateDrawer
          current={{ id: caseData.id, caseNumber: caseData.caseNumber, status: caseData.status, isPrimaryData: (caseData as any).isPrimaryData, dataBasis: (caseData as any).dataBasis, incidentDate: caseData.incidentDate, country: caseData.country, region: caseData.region, city: caseData.city, violationTypes: caseData.violationTypes, createdAt: (caseData as any).createdAt }}
          mode={markMode}
          onClose={() => setMarkMode(null)}
          onCreated={(m, note) => { setMarkMode(null); if (note) showToast('success', note); navigate(`/merge/${m.id}`); }}
        />
      )}

      {/* AI card (Figma "AI review context"): what the AI did on this case and what is still open,
          in one place. Left: the verification summary (the scores open its details drawer, the
          "why" line under them, or the run controls). Right: the readiness list; each open item
          links to its card or opens the AI drawer on its tab. Same three columns and gap as the
          page grid below, with the divider centred in the gap; at that width the columns carry
          the card's side padding. */}
      {showAiCard && (
        <div id="section-ai-validation" className="mb-6 rounded-[10px] border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-[18px] py-5 grid gap-5 lg:px-0 lg:grid-cols-3 lg:gap-8">
          {(() => {
            const heading = (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-gray-500 dark:text-gray-400" title={caseData.isAutoSourced ? "This case was created by IPRI's AI-assisted scraper from public sources. All content is subject to human review before approval." : undefined}>
                <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {caseData.isAutoSourced ? 'AI Assisted Case' : 'AI Assisted Verification'}
              </span>
            );
            // No decision chip or field count here: the open items on the right say what stands
            // between the case and approval, and the decision is in the drawer.
            return canSeeAiPanel ? (
              <div className="min-w-0 lg:col-span-2 lg:px-6">
                <AiValidationPanel embedded heading={heading} ref={aiPanelRef} caseId={id!} caseData={caseData as Record<string, any>} onChange={fetchCase} onSummary={handleValidationSummary} lastEditAt={lastEditAt} decidedAt={decidedAt} onJumpToField={handleJumpToField} onShowEvidence={() => scrollToSection('section-evidence', getHeaderOffset(), { flash: true })} />
              </div>
            ) : (
              <div className="min-w-0 flex items-center lg:col-span-2 lg:px-6">{heading}</div>
            );
          })()}
          {showReadiness && (
            <div className="min-w-0 text-sm border-t border-gray-200 dark:border-gray-700 pt-4 lg:border-t-0 lg:pt-0 lg:border-l lg:-ml-4 lg:pl-10 lg:pr-6" role="status">
              <div className="flex items-start gap-2.5">
                <ExclamationTriangleIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${readinessBlocking ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} aria-hidden="true" />
                <div className="min-w-0">{readinessList(true)}</div>
              </div>
            </div>
          )}
          {/* Nothing open after a run: say so, or an empty column reads as a list that failed to
              load. Neutral wording — it is the AI side being clear, not a verdict on the case. The
              link is the way back to what was applied or resolved, with its Undo. */}
          {!showReadiness && validationSummary?.decision && !validationSummary.isValidating && (
            <div className="min-w-0 text-sm border-t border-gray-200 dark:border-gray-700 pt-4 lg:border-t-0 lg:pt-0 lg:border-l lg:-ml-4 lg:pl-10 lg:pr-6 flex items-start gap-2.5 text-gray-600 dark:text-gray-300" role="status">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-gray-400" aria-hidden="true" />
              <div className="min-w-0">
                <p>Nothing left to act on from the AI check.</p>
                {canSeeAiPanel && (
                  <button
                    type="button"
                    onClick={() => aiPanelRef.current?.openDrawer('all')}
                    title="What the AI filled in or flagged, with Undo on each applied value"
                    className="mt-1 text-xs leading-5 font-medium text-blue-700 dark:text-blue-300 hover:underline"
                  >
                    See the details
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Readiness without an AI card to hold it (a case with no AI involvement but, say, a
          possible duplicate pending review): the stand-alone banner, first thing under the header. */}
      {showReadiness && !showAiCard && (
        <div
          className={`mb-6 rounded-md border px-4 py-3 flex items-start gap-3 text-sm ${
            readinessBlocking
              ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-900 dark:text-red-200'
              : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200'
          }`}
          role="status"
        >
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0">{readinessList(false)}</div>
        </div>
      )}

      {submitErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-red-800">Case is not complete enough to submit</h3>
                <p className="text-sm text-red-700 mt-1">Please fix the following issues before submitting:</p>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                  {submitErrors.map((err, i) => (
                    <li key={i}>{err.message}</li>
                  ))}
                </ul>
              </div>
            </div>
            <button
              onClick={() => setSubmitErrors([])}
              aria-label="Dismiss"
              className="flex-shrink-0 text-red-500 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Incident Details */}
          <div id="section-incident" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Incident Details</h2>
            <div className="space-y-4">
              {caseData.description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Description{aiMark('description')}</h3>
                  <MarkdownText text={caseData.description} />
                </div>
              )}
              {(caseData.isCaseOngoing !== undefined || caseData.caseStartDate || caseData.caseEndDate || caseData.incidentTime) && (
                <DefGrid className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-3">
                  <Def label="Started" mark={aiMark('caseStartDate')} value={caseData.caseStartDate ? formatCaseDate(caseData.caseStartDate, caseData.caseStartDatePrecision) : <span className="italic text-gray-400">Not recorded</span>} />
                  <Def
                    label={caseData.isCaseOngoing === true ? 'Status' : 'Ended'}
                    value={
                      caseData.isCaseOngoing === true ? 'Ongoing'
                      : caseData.caseEndDate ? formatCaseDate(caseData.caseEndDate)
                      : caseData.isCaseOngoing === false ? <span className="italic text-gray-400">Concluded, date not recorded</span>
                      : <span className="italic text-gray-400">Not recorded</span>
                    }
                  />
                  <Def label="Time" mark={aiMark('incidentTime')} value={caseData.incidentTime} />
                </DefGrid>
              )}
              <NotDocumented rows={gaps.incident} caseId={id!} canEdit={gapsEditable} className="mt-0" />
            </div>
          </div>

          {/* Location */}
          <div id="section-location" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Location
            </h2>

            <DefGrid>
              <Def label="Country" mark={aiMark('country')} value={caseData.country} />
              <Def label="Region" mark={aiMark('region')} value={caseData.region} />
              <Def label="City" mark={aiMark('city')} value={caseData.city} />
              <Def label="Specific location" mark={aiMark('specificLocation')} value={caseData.specificLocation} wide />
            </DefGrid>
            {caseData.latitude && caseData.longitude && (
              <div className="mt-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1">
                    <MapPinIcon className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                    <span className="tabular-nums">{caseData.latitude.toFixed(5)}, {caseData.longitude.toFixed(5)}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowMap((v) => !v)}
                    aria-expanded={showMap}
                    className="font-medium text-gray-700 dark:text-gray-300 underline hover:text-gray-900 dark:hover:text-gray-100"
                  >
                    {showMap ? 'Hide map' : 'Show map'}
                  </button>
                </div>
                {showMap && (
                  <div className="mt-3">
                    <MapView
                      latitude={caseData.latitude}
                      longitude={caseData.longitude}
                      address={caseData.specificLocation}
                      height="250px"
                    />
                  </div>
                )}
              </div>
            )}
            <NotDocumented rows={gaps.location} caseId={id!} canEdit={gapsEditable} />
          </div>

          {/* Indigenous Group */}
          <div id="section-indigenous-group" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Indigenous Group Information</h2>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Indigenous Group:</span>{aiMark('indigenousGroup')} {caseData.indigenousGroup}
              </div>
              {caseData.indigenousTerritory && (
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Territory:</span>{aiMark('indigenousTerritory')} {caseData.indigenousTerritory}
                </div>
              )}
            </div>
            <NotDocumented rows={gaps.indigenousGroup} caseId={id!} canEdit={gapsEditable} />
          </div>

          {/* Victims */}
          <div id="section-victims" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Victims</h2>
            <div className="space-y-4">
              {caseData.victims.some(v => !v.isGroup) && (
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-1">Individual victims</h3>
              )}
              {caseData.victims.some(v => !v.isGroup) && (
                <div className="border border-gray-200 dark:border-gray-700 border-l-4 border-l-gray-400 dark:border-l-gray-500 bg-white dark:bg-gray-800/40 rounded-lg p-4 space-y-3">
                  {caseData.victims.filter(v => !v.isGroup).map((victim, victimIdx) => (
                <div key={victim.id} id={`victim-${victim.id}`} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {hideIds ? <span className="italic text-gray-600 dark:text-gray-400" title="Name hidden on this restricted case">Victim {victimIdx + 1}</span> : <>{victim.firstName} {victim.lastName}</>}
                      {aiMark(`victims[${victim.id}].firstName`)}{aiMark(`victims[${victim.id}].lastName`)}
                      {victim.isIndigenous === false && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">Non-Indigenous{aiMark(`victims[${victim.id}].isIndigenous`)}</span>}
                    </h4>
                    {Array.isArray(victim.violationDetails) && victim.violationDetails.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {victim.violationDetails.map((t, ti) => (
                          <span key={ti} title={describeValue('violationTypes', t.otherViolationType || t.violationType)} className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getViolationColor(t.otherViolationType || t.violationType).soft}`}>
                            {getViolationTypeLabel((t.otherViolationType || t.violationType) || '')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <DefGrid className="mt-3">
                    <Def label="Gender" mark={aiMark(`victims[${victim.id}].gender`)} value={victim.gender ? getViolationTypeLabel(victim.gender) : undefined} />
                    <Def label="Age bracket" mark={aiMark(`victims[${victim.id}].ageBracket`)} value={victim.ageBracket ? ({ A: '0–12 years', B: '13–25 years', C: '26–35 years', D: '36–59 years', E: '60+ years' } as Record<string, string>)[victim.ageBracket] || victim.ageBracket : undefined} />
                    <Def label="Date of birth" mark={aiMark(`victims[${victim.id}].dateOfBirth`)} value={victim.dateOfBirth && !hideIds ? formatCaseDate(victim.dateOfBirth) : undefined} />
                    <Def label="Indigenous group" mark={aiMark(`victims[${victim.id}].indigenousGroupOfVictim`)} value={victim.indigenousGroupOfVictim} />
                    <Def label="Community role" mark={aiMark(`victims[${victim.id}].communityRole`)} value={victim.communityRole} />
                    <Def label="Role category" value={victim.communityRoleType ? <span title={describeValue('communityRoleType', victim.communityRoleType)}>{optionLabel(COMMUNITY_ROLE_TYPES, victim.communityRoleType)}</span> : undefined} />
                    <Def label="Dependents" mark={aiMark(`victims[${victim.id}].dependentType`)} value={victim.dependentType ? ({ WD: 'With dependents', WOD: 'Without dependents', ND: 'No data' } as Record<string, string>)[victim.dependentType] || victim.dependentType : undefined} />
                    <Def label="Dependent details" mark={aiMark(`victims[${victim.id}].dependentDetails`)} value={victim.dependentDetails ? <MarkdownText text={victim.dependentDetails} /> : undefined} wide />
                    <Def label="Additional info" mark={aiMark(`victims[${victim.id}].additionalInfo`)} value={victim.additionalInfo} wide />
                  </DefGrid>
                  {outcomeRows(victim.violationDetails).map(([label, row]) => (<CrimOutcomeSummary key={label} label={label} row={row} />))}
                  <NotDocumented rows={gaps.victim(victim)} caseId={id!} canEdit={gapsEditable} className="mt-3" />
                </div>
              ))}
                </div>
              )}
              {caseData.victims.some(v => v.isGroup) && (
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-1">Groups of victims</h3>
              )}
              {caseData.victims.filter(v => v.isGroup).sort((a, b) => Number(a.isIndigenous === false) - Number(b.isIndigenous === false)).map((gv) => {
                const indigenous = gv.isIndigenous !== false;
                return (
                <div key={gv.id} id={`victim-${gv.id}`} className="border border-gray-200 dark:border-gray-700 border-l-4 border-l-gray-400 dark:border-l-gray-500 bg-white dark:bg-gray-800/40 rounded-lg p-4 space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{indigenous ? 'Indigenous group' : 'Non-Indigenous group'}{!indigenous && aiMark(`victims[${gv.id}].isIndigenous`)}</h3>
                  {gv.groupType && (
                    <div>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Type of Group:</span>
                      <span title={describeValue('groupType', gv.groupType)} className="ml-2 px-3 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">{optionLabel(VICTIM_GROUP_TYPES, gv.groupType)}</span>
                    </div>
                  )}
                  {gv.groupName && Array.isArray(gv.groupName) && gv.groupName.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {gv.groupName.map((group: string) => (
                        <span key={group} className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">{group}</span>
                      ))}
                    </div>
                  )}
                  {(gv.affectedIndividualsCount || gv.affectedFamiliesCount || gv.affectedHomesCount ||
                    gv.affectedGroupsCount || gv.affectedCommunitiesCount || gv.affectedVillagesCount) && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Affected Population</h4>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {gv.affectedIndividualsCount != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affectedIndividualsCount}</div><div className="text-xs text-gray-600 dark:text-gray-400">Individuals</div></div>)}
                        {gv.affectedFamiliesCount != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affectedFamiliesCount}</div><div className="text-xs text-gray-600 dark:text-gray-400">Families</div></div>)}
                        {gv.affectedHomesCount != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affectedHomesCount}</div><div className="text-xs text-gray-600 dark:text-gray-400">Homes</div></div>)}
                        {gv.affectedGroupsCount != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affectedGroupsCount}</div><div className="text-xs text-gray-600 dark:text-gray-400">Groups</div></div>)}
                        {gv.affectedCommunitiesCount != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affectedCommunitiesCount}</div><div className="text-xs text-gray-600 dark:text-gray-400">Communities</div></div>)}
                        {gv.affectedVillagesCount != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affectedVillagesCount}</div><div className="text-xs text-gray-600 dark:text-gray-400">Villages</div></div>)}
                      </div>
                    </div>
                  )}
                  {Array.isArray(gv.violationDetails) && gv.violationDetails.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Breakdown by Violation</span>
                      {/* One badge above the scrollable table — an inline mark inside overflow-x-auto gets its popup clipped. */}
                      {aiMark(gv.violationDetails.map((t: any) => `victims[${gv.id}].violation[${t.otherViolationType || t.violationType}].affectedCount`))}
                      <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        {(() => {
                          const cols = ['affectedCount','affectedMaleCount','affectedFemaleCount','affected0to12Count','affected13to25Count','affected26to35Count','affected36to59Count','affected60PlusCount'];
                          const heads = ['Total','Male','Female','0–12','13–25','26–35','36–59','60+'];
                          const rows = gv.violationDetails as any[];
                          const totals = cols.map((c) => rows.reduce<number | null>((acc, r) => (r[c] == null ? acc : (acc ?? 0) + Number(r[c])), null));
                          const cell = (v: any) => v == null ? <span className="text-gray-300 dark:text-gray-600">—</span> : v;
                          const stickyCell = 'sticky left-0 bg-white dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700';
                          return (
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800/50">
                            <tr className="text-gray-500 dark:text-gray-400">
                              <th className={`px-4 py-2.5 font-medium text-left ${stickyCell} bg-gray-50 dark:bg-gray-800`}>Violation</th>
                              {heads.map((h) => <th key={h} className="px-3 py-2.5 font-medium text-right whitespace-nowrap">{h}</th>)}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {rows.map((t: any, ti: number) => {
                              const key = t.otherViolationType || t.violationType;
                              return (
                                <tr key={ti}>
                                  <td className={`px-4 py-2.5 whitespace-nowrap ${stickyCell}`}>
                                    <span title={describeValue('violationTypes', key)} className={`inline-block px-2 py-0.5 text-xs rounded-full border ${getViolationColor(key).soft}`}>{getViolationTypeLabel(key || '')}</span>
                                  </td>
                                  {cols.map((c, ci) => (
                                    <td key={c} className={`px-3 py-2.5 text-right tabular-nums ${ci === 0 ? 'font-bold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>{cell(t[c])}</td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                          {rows.length > 1 && (
                            <tfoot className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
                              <tr className="text-gray-900 dark:text-gray-100">
                                <td className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${stickyCell} bg-gray-50 dark:bg-gray-800`}>All violations</td>
                                {totals.map((v, ci) => <td key={ci} className="px-3 py-2.5 text-right tabular-nums font-semibold">{cell(v)}</td>)}
                              </tr>
                            </tfoot>
                          )}
                        </table>
                          );
                        })()}
                      </div>
                      {outcomeRows(gv.violationDetails).map(([label, row]) => (<CrimOutcomeSummary key={label} label={label} row={row} />))}
                    </div>
                  )}
                  {(!Array.isArray(gv.violationDetails) || gv.violationDetails.length === 0) && (gv.affectedMaleCount || gv.affectedFemaleCount) && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Gender Breakdown</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {gv.affectedMaleCount != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affectedMaleCount}</div><div className="text-xs text-gray-600 dark:text-gray-400">Male</div></div>)}
                        {gv.affectedFemaleCount != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affectedFemaleCount}</div><div className="text-xs text-gray-600 dark:text-gray-400">Female</div></div>)}
                      </div>
                    </div>
                  )}
                  {(!Array.isArray(gv.violationDetails) || gv.violationDetails.length === 0) && (gv.affected0to12Count || gv.affected13to25Count || gv.affected26to35Count ||
                    gv.affected36to59Count || gv.affected60PlusCount) && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Age Breakdown</h4>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {gv.affected0to12Count != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affected0to12Count}</div><div className="text-xs text-gray-600 dark:text-gray-400">0-12 years</div></div>)}
                        {gv.affected13to25Count != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affected13to25Count}</div><div className="text-xs text-gray-600 dark:text-gray-400">13-25 years</div></div>)}
                        {gv.affected26to35Count != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affected26to35Count}</div><div className="text-xs text-gray-600 dark:text-gray-400">26-35 years</div></div>)}
                        {gv.affected36to59Count != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affected36to59Count}</div><div className="text-xs text-gray-600 dark:text-gray-400">36-59 years</div></div>)}
                        {gv.affected60PlusCount != null && (<div className="text-center p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"><div className="text-lg font-bold text-gray-900 dark:text-gray-100">{gv.affected60PlusCount}</div><div className="text-xs text-gray-600 dark:text-gray-400">60+ years</div></div>)}
                      </div>
                    </div>
                  )}
                  {gv.additionalInfo && (
                    <DefGrid>
                      <Def label="Additional info" mark={aiMark(`victims[${gv.id}].additionalInfo`)} value={gv.additionalInfo} wide />
                    </DefGrid>
                  )}
                  <NotDocumented rows={gaps.victim(gv)} caseId={id!} canEdit={gapsEditable} className="mt-0" />
                </div>
                );
              })}
            </div>
          </div>

          {/* Violation cards — one shared component; see components/ViolationDetailCard.tsx */}
          {crimDetail && (
            <ViolationDetailCard
              id="section-criminalization"
              kind="CRIMINALIZATION"
              title="Criminalization Details"
              detail={crimDetail as Record<string, any>}
              aiMark={aiMark}
              extra={crimOutcomeRows.length > 0 ? (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Outcomes by victim</h3>
                  <VictimOutcomesTable rows={crimOutcomeRows} onJump={(vid) => scrollToSection(`victim-${vid}`, getHeaderOffset(), { flash: true })} />
                </div>
              ) : undefined}
              footer={<NotDocumented rows={gaps.violation(crimDetail as Record<string, any>)} caseId={id!} canEdit={gapsEditable} className="mt-0" />}
            />
          )}
          {killDetail && (
            <ViolationDetailCard
              id="section-killing"
              kind="KILLING"
              title="Killing Details"
              detail={killDetail as Record<string, any>}
              aiMark={aiMark}
              footer={<NotDocumented rows={gaps.violation(killDetail as Record<string, any>)} caseId={id!} canEdit={gapsEditable} className="mt-0" />}
            />
          )}
          {hasOtherData && (
            <ViolationDetailCard
              id="section-other-violations"
              kind="OTHER"
              title="Other Human Rights Violations"
              detail={otherDetail as Record<string, any> | undefined}
              otherViolationTypes={caseData.otherViolationTypes}
              otherViolationTypeDetails={caseData.otherViolationTypeDetails}
              aiMark={aiMark}
              footer={<NotDocumented rows={otherDetail ? gaps.violation(otherDetail as Record<string, any>) : []} caseId={id!} canEdit={gapsEditable} className="mt-0" />}
            />
          )}

          {/* Perpetrators */}
          {showPerpetrators && (
            <div id="section-perpetrators" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Perpetrators</h2>
              <div className="space-y-4">
                {caseData.perpetrators.map((perpetrator) => (
                  <div key={perpetrator.id} id={`perpetrator-${perpetrator.id}`} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{perpetrator.name}</h4>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Type:</span> {perpetrator.type ? (PERPETRATOR_TYPES.find((t) => t.value === perpetrator.type)?.label || perpetrator.type) : <span className="italic text-gray-400">Not recorded</span>}
                      <span className="ml-4 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">{perpetrator.isOrganization ? 'Organisation' : 'Individual'}</span>
                    </div>
                    {(perpetrator as any).organizationHead && (<div className="text-sm text-gray-600 dark:text-gray-400 mt-1"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Head of organisation:</span> {(perpetrator as any).organizationHead}</div>)}
                    {(perpetrator as any).position && (<div className="text-sm text-gray-600 dark:text-gray-400 mt-1"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Position:</span> {(perpetrator as any).position}</div>)}
                    {perpetrator.affiliation && (<div className="text-sm text-gray-600 dark:text-gray-400 mt-1"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Affiliation:</span> {perpetrator.affiliation}</div>)}
                    {perpetrator.notes && (<div className="mt-2"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Notes:</span><MarkdownText text={perpetrator.notes} /></div>)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Impact Assessment */}
          {showImpact && (
            <div id="section-impact" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Impact Assessment</h2>
              <div className="space-y-4">
                {caseData.impactTypes && caseData.impactTypes.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Types of Impact</h3>
                    <div className="flex flex-wrap gap-2">
                      {caseData.impactTypes.map(t => (
                        <span key={t} title={describeValue('impactTypes', t)} className="px-3 py-1 rounded-lg text-sm bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">{optionLabel(IMPACT_TYPES, t)}</span>
                      ))}
                    </div>
                  </div>
                )}
                {caseData.familyImpact && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Family Impact{aiMark('familyImpact')}</h3>
                    <MarkdownText text={caseData.familyImpact} />
                  </div>
                )}
                {caseData.communityImpact && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Community Impact{aiMark('communityImpact')}</h3>
                    <MarkdownText text={caseData.communityImpact} />
                  </div>
                )}
                <NotDocumented rows={gaps.impact} caseId={id!} canEdit={gapsEditable} className="mt-0" />
              </div>
            </div>
          )}

          {/* Development Project Information */}
          {showDevelopmentProject && (
            <div id="section-development-project" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">{projectRows.length > 1 ? 'Development Projects' : 'Development Project Information'}</h2>
              <div className="space-y-3">
                {caseData.isRelatedToDevelopmentProject && (
                  <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{projectRows.length > 1 ? `This case is related to ${projectRows.length} development projects` : 'This case is related to a development project'}</p>
                  </div>
                )}
                {projectRows.map((p, i) => (
                  <div key={p.id || i} id={`development-project-${p.id || i}`} className={projectRows.length > 1 ? 'border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2' : 'space-y-3'}>
                    {projectRows.length > 1 && (<p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{i === 0 ? 'Primary project' : `Project ${i + 1}`}</p>)}
                    {p.type && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Project Type:</span> <span title={describeValue('developmentProjectType', p.type)}>{defineValue('developmentProjectType', p.type)?.label || p.type}</span></div>)}
                    {(p.subtypes || []).length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Subtypes:</span>
                        {(p.subtypes || []).map((sub) => (
                          <span key={sub} title={describeValue('developmentProjectSubtype', sub)} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">{defineValue('developmentProjectSubtype', sub)?.label || sub}</span>
                        ))}
                      </div>
                    )}
                    {p.name && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Project Name:</span> {p.name}</div>)}
                    {p.description && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Description:</span><MarkdownText text={p.description} className="mt-1 text-sm text-gray-700 dark:text-gray-300" /></div>)}
                    {p.startYear && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Start Year:</span> {p.startYear}</div>)}
                    {p.source && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Source:</span><p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{p.source}</p></div>)}
                    {p.websiteLink && (<div><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Website:</span><a href={p.websiteLink} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:text-blue-800 underline break-all">{p.websiteLink}</a></div>)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Investigations */}
          {showInvestigations && (
            <div id="section-investigations" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Investigations</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Investigation Conducted:</span>{aiMark('investigationConducted')}
                  <span className={`ml-2 px-3 py-1 rounded-lg text-xs font-medium ${yesNoClass(caseData.investigationConducted, 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200', 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200')}`}>
                    {yesNo(caseData.investigationConducted, 'Yes', 'No')}
                  </span>
                </div>
                {caseData.investigationConducted && caseData.investigationDetails && (
                  <MarkdownText text={caseData.investigationDetails} lead={aiMark('investigationDetails')} />
                )}
                {!caseData.investigationConducted && caseData.investigationWhyNot && (
                  <div>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>{aiMark('investigationWhyNot')}
                    <MarkdownText text={caseData.investigationWhyNot} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Advocacy Actions */}
          {showAdvocacy && (
            <div id="section-advocacy" className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Advocacy & Response Actions</h2>
              {caseData.advocacyUndertaken !== undefined && (
                <div className="mb-3">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Advocacy Undertaken:</span>{aiMark('advocacyUndertaken')}
                  <span className={`ml-2 px-3 py-1 rounded-lg text-xs font-medium ${yesNoClass(caseData.advocacyUndertaken, 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200', 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200')}`}>
                    {yesNo(caseData.advocacyUndertaken, 'Yes', 'No')}
                  </span>
                </div>
              )}
              {caseData.advocacyUndertaken && caseData.advocacyDetails && (
                <MarkdownText text={caseData.advocacyDetails} lead={aiMark('advocacyDetails')} />
              )}
              {!caseData.advocacyUndertaken && caseData.advocacyWhyNot && (
                <div className="mt-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Why Not:</span>{aiMark('advocacyWhyNot')}
                  <MarkdownText text={caseData.advocacyWhyNot} />
                </div>
              )}
            </div>
          )}

          {/* Sources & Documents — the sources (with a per-field trace of what each supports) and the
              uploaded documents. After the narrative, since checking claims against sources is the
              review; the AI card links straight here. Not called "Evidence": the AI drawer uses that
              word for the passage behind a finding, and news links are not proof. */}
          <EvidenceCard
            caseId={id!}
            caseData={caseData as Record<string, any>}
            documents={documents}
            documentsLoading={documentsLoading}
            onDownload={downloadDocument}
            validation={validationSummary}
            sourceGaps={gaps.sources}
            canEdit={gapsEditable}
            canUpload={canUploadDocuments()}
            onDocumentsChange={fetchDocuments}
            onJumpToField={handleJumpToField}
          />

          {/* Data Source & Documenter — provenance and consent metadata, folded away by default so the
              narrative comes first. The folded summary names the documenter; origin, data basis and
              restriction are not repeated here because the page header already carries them. */}
          {showDataSource && (
            <CollapsibleCard
              id="section-data-source"
              title="Data Source & Documenter"
              storageKey="caseDetail.dataSourceOpen"
              summary={
                !caseData.isAutoSourced && caseData.documentorName ? (
                  <span className="text-gray-700 dark:text-gray-300">
                    Documented by <span className="font-medium text-gray-900 dark:text-gray-100">{caseData.documentorName}</span>
                    {caseData.documentorOrganization ? `, ${caseData.documentorOrganization}` : ''}
                  </span>
                ) : undefined
              }
            >
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                {caseData.isAutoSourced && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Origin</dt>
                    <dd className="mt-1">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800"
                        title="This case was created by IPRI's AI-assisted scraper from public sources. All content is subject to human review before approval."
                      >
                        <SparklesIcon className="h-3 w-3" />
                        Generated with AI assistance
                      </span>
                    </dd>
                  </div>
                )}
                {!caseData.isAutoSourced && caseData.documentorName && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Documented By</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{caseData.documentorName}</dd>
                  </div>
                )}
                {!caseData.isAutoSourced && caseData.documentorOrganization && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Organization</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{caseData.documentorOrganization}</dd>
                  </div>
                )}
                {!caseData.isAutoSourced && caseData.documentorEmail && !hideIds && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100 break-words">{caseData.documentorEmail}</dd>
                  </div>
                )}
                {!caseData.isAutoSourced && caseData.documentorPhone && !hideIds && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{caseData.documentorPhone}</dd>
                  </div>
                )}
                {basisFromCase(caseData) && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Type</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`} title={DATA_BASIS_TITLE[basisFromCase(caseData)!]}>
                        {DATA_BASIS_LABEL[basisFromCase(caseData)!]}
                      </span>
                    </dd>
                  </div>
                )}
                {caseData.dataSource && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Source</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">{caseData.dataSource}</dd>
                  </div>
                )}
                {caseData.dataUsageRestriction && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Usage Restriction</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">
                      {caseData.dataUsageRestriction === 'IPRI_ONLY_TRENDS' && 'IPRI Only - Trends Analysis'}
                      {caseData.dataUsageRestriction === 'IPRI_WITH_DETAILS' && 'IPRI With Details - Can include case details publicly'}
                    </dd>
                  </div>
                )}
                {caseData.willingToShareMoreInfo !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Willing to Share More Info</dt>
                    <dd className={`text-sm font-medium ${yesNoClass(caseData.willingToShareMoreInfo, 'text-gray-900 dark:text-gray-100', 'text-gray-500', 'text-gray-400 italic')}`}>
                      {yesNo(caseData.willingToShareMoreInfo, 'Yes', 'No')}
                    </dd>
                  </div>
                )}
                {caseData.isLDSFApplicant !== undefined && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">LDSF Applicant</dt>
                    <dd className={`text-sm font-medium ${yesNoClass(caseData.isLDSFApplicant, 'text-gray-900 dark:text-gray-100', 'text-gray-500', 'text-gray-400 italic')}`}>
                      {yesNo(caseData.isLDSFApplicant, 'Yes', 'No')}
                    </dd>
                  </div>
                )}
                {caseData.ldsfPaymentCompletedDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">LDSF Payment Completed Date</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100">
                      {formatCaseDate(caseData.ldsfPaymentCompletedDate)}
                    </dd>
                  </div>
                )}
              </dl>
            </CollapsibleCard>
          )}

          {/* Auto-filled fields with no inline anchor — catch-all at the bottom of the case details */}
          {aiLeftovers()}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Proposed updates from related sources — clickable card → right slide-over */}
          <SuggestionsCard />

          {/* Case Information - Editable */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Case Information</h3>
              {canUpdateStatus() && !editingCaseInfo && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingCaseInfo(true);
                    setEditPriority(caseData.priority);
                    setEditReportedDate(caseData.reportedDate ? new Date(caseData.reportedDate).toISOString().split('T')[0] : '');
                    setEditAssignee(caseData.assignedTo?.id || '');
                    setEditTags(caseData.tags ?? []);
                    fetchAvailableUsers();
                  }}
                  className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                >
                  Edit
                </button>
              )}
            </div>

            {editingCaseInfo ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Priority</label>
                  <select
                    value={editPriority}
                    onChange={(e) => setEditPriority(parseInt(e.target.value))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                  >
                    <option value={1}>Low</option>
                    <option value={2}>Med-low</option>
                    <option value={3}>Medium</option>
                    <option value={4}>Med-high</option>
                    <option value={5}>High</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned To</label>
                  <select
                    value={editAssignee}
                    onChange={(e) => setEditAssignee(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                  >
                    <option value="">Unassigned</option>
                    {availableUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.role})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Date of Submission</label>
                  <input
                    type="date"
                    value={editReportedDate}
                    onChange={(e) => setEditReportedDate(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Tags</label>
                  <div className="mt-1">
                    <TagInput tags={editTags} onChange={setEditTags} placeholder="Add a tag" suggestions={SUGGESTED_CASE_TAGS} />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={async () => {
                      if (editPriority !== caseData.priority) await updateCasePriority(editPriority);

                      const currentAssigneeId = caseData.assignedTo?.id || '';
                      if (editAssignee !== currentAssigneeId) await handleAssignment(editAssignee || null);

                      const currentReportedDate = caseData.reportedDate ? new Date(caseData.reportedDate).toISOString().split('T')[0] : '';
                      const patch: Record<string, unknown> = {};
                      if (editReportedDate !== currentReportedDate) patch.reportedDate = editReportedDate;
                      const cleanTags = Array.from(new Set(editTags.map((t) => t.trim()).filter(Boolean)));
                      if (JSON.stringify(cleanTags) !== JSON.stringify(caseData.tags ?? [])) patch.tags = cleanTags;
                      if (Object.keys(patch).length > 0) {
                        setUpdating(true);
                        try {
                          const response = await API.put(`/api/cases/${id}`, patch);
                          setCaseData(response.data.case);
                        } catch (err: any) {
                          showToast('error', err.response?.data?.message || 'Failed to update case information');
                        } finally {
                          setUpdating(false);
                        }
                      }
                      // Update responses carry a thinner case (no submitter role, for one), so re-read
                      // the full record once the edits are in.
                      await fetchCase();
                      setEditingCaseInfo(false);
                    }}
                    disabled={updating}
                    className="flex-1 px-3 py-2 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {updating ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingCaseInfo(false)}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="mt-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyle(caseData.status)}`}>
                      {statusLabel(caseData.status)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Priority</dt>
                  <dd className="mt-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}>
                      {getPriorityLabel(caseData.priority)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Tags</dt>
                  <dd className="mt-1">
                    {caseData.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {caseData.tags.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">{tag}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">None</span>
                    )}
                  </dd>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Submitted By</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                    {caseData.submittedBy.firstName} {caseData.submittedBy.lastName}
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{caseData.submittedBy.role}</span>
                  </dd>
                </div>
                {caseData.reviewedBy && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Reviewed By</dt>
                    <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">{caseData.reviewedBy.firstName} {caseData.reviewedBy.lastName}</dd>
                  </div>
                )}
                {caseData.status === 'REJECTED' && caseData.rejectionReason && (
                  <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">Rejection reason</dt>
                    {caseData.rejectionCategory && (
                      <dd className="mt-1"><span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">{caseData.rejectionCategory}</span></dd>
                    )}
                    <dd className="mt-1"><MarkdownText text={caseData.rejectionReason} className="text-sm text-gray-800 dark:text-gray-200" /></dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned To</dt>
                  <dd className="mt-1">
                    {caseData.assignedTo ? (
                      <>
                        <span className="text-sm text-gray-900 dark:text-gray-100">{caseData.assignedTo.firstName} {caseData.assignedTo.lastName}</span>
                        <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{caseData.assignedTo.role}</span>
                        {caseData.assignedAt && (
                          <span className="block text-xs text-gray-500 mt-1">Assigned {formatCaseDate(caseData.assignedAt)}</span>
                        )}
                      </>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500">Unassigned</span>
                    )}
                  </dd>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Date of Submission</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">{formatCaseDate(caseData.reportedDate)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created in system</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">{formatCaseDate(caseData.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last edited</dt>
                  <dd className="text-sm text-gray-900 dark:text-gray-100 mt-1">{formatCaseDate(caseData.updatedAt)}</dd>
                </div>
              </dl>
            )}

            {/* After a decision the AI verification is part of the record, not a review surface:
                a section of Case Information, with the case's origin tagged. The panel mounts here
                (instead of in the AI card above) so its drawer and summary stay available. */}
            {isEndState(caseData.status) && (caseData.isAutoSourced || canSeeAiPanel) && (() => {
              const heading = (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI Verification Information</h4>
                  {caseData.isAutoSourced && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" title="This case was created by IPRI's AI-assisted scraper from public sources. A person reviewed it before the decision.">
                      <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      AI Assisted Case
                    </span>
                  )}
                </div>
              );
              return (
                <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                  {canSeeAiPanel ? (
                    <AiValidationPanel embedded heading={heading} ref={aiPanelRef} caseId={id!} caseData={caseData as Record<string, any>} onChange={fetchCase} onSummary={handleValidationSummary} lastEditAt={lastEditAt} decidedAt={decidedAt} onJumpToField={handleJumpToField} onShowEvidence={() => scrollToSection('section-evidence', getHeaderOffset(), { flash: true })} />
                  ) : heading}
                </div>
              );
            })()}
          </div>

          {/* Duplicates, one card: the checker (its matches, for every reviewer role to decide; the
              Run check button inside it is a paid AI operation, ADMIN/APPROVER only) and under it
              the most similar cases — what the engine ranks closest, for the reviewer to check.
              The list lists, never decides; it is off on archived cases. */}
          {(() => {
            const canSeeChecker = user?.role === 'ADMIN' || user?.role === 'APPROVER' || user?.role === 'RESEARCHER';
            const canSeeSimilar = canSeeChecker && caseData.status !== 'ARCHIVED';
            return (canSeeChecker || canSeeSimilar) && (
              <div id="section-dedup">
                <DeduplicationPanel
                  caseId={id!}
                  onSummary={handleDedupSummary}
                  onChange={fetchCase}
                  showChecker={canSeeChecker}
                  showSimilar={canSeeSimilar}
                  similarRefreshKey={(caseData as any).updatedAt}
                />
              </div>
            );
          })()}

          {/* Activity — comments, workflow/edit events, AI and duplicate-check runs, in one timeline */}
          <ActivityCard
            caseId={id!}
            refreshKey={commentsRefreshKey}
            canViewAudit={user?.role === 'ADMIN' || user?.role === 'APPROVER' || user?.role === 'RESEARCHER'}
            validationRuns={validationSummary?.history ?? []}
            dedupRuns={dedupSummary.runs}
            onLastEditAt={handleLastEditAt}
            onDecidedAt={handleDecidedAt}
          />

        </div>
      </div>
    </div>

    {/* Way back to the drawer a field jump came from */}
    {reviewReturn && (
      <div className="fixed bottom-6 right-6 z-40 flex items-center gap-1 rounded-full bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 shadow-lg pl-4 pr-1.5 py-1.5 text-sm" role="status">
        <button type="button" onClick={returnToReview} className="font-medium hover:underline">
          ← Back to {reviewReturn.origin.kind === 'ai' ? 'AI review' : 'suggestions'}
        </button>
        <button type="button" onClick={() => setReviewReturn(null)} aria-label="Dismiss" className="rounded-full p-1 hover:bg-white/20 dark:hover:bg-black/10">
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    )}

    {/* Return-for-research modal — a required note, posted as an internal comment */}
    {returnOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Return for research</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Tell the researcher what still needs work. This is posted as an internal comment.</p>
          </div>
          <div className="px-5 py-4">
            <label htmlFor="return-note" className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Note <span className="text-red-500">*</span></label>
            <textarea
              id="return-note"
              value={returnNote}
              onChange={(e) => setReturnNote(e.target.value)}
              rows={4}
              autoFocus
              placeholder="What should the researcher check, add, or correct?"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button
              onClick={() => setReturnOpen(false)}
              disabled={updating}
              className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={returnForResearch}
              disabled={updating || !returnNote.trim()}
              className="px-4 py-2 text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
            >
              {updating ? 'Returning…' : 'Return for research'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Reject modal — a required ground + free-text note, recorded on the case */}
    {rejectOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reject case</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Record why this case is being rejected. This is kept on the case.</p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Ground</label>
              <select
                value={rejectCategory}
                onChange={(e) => setRejectCategory(e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              >
                <option value="">Select a ground…</option>
                {REJECTION_GROUNDS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Reason <span className="text-red-500">*</span></label>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Explain why this case cannot be published…"
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button
              onClick={() => setRejectOpen(false)}
              disabled={updating}
              className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={rejectCase}
              disabled={updating || !rejectNote.trim()}
              className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {updating ? 'Rejecting…' : 'Reject case'}
            </button>
          </div>
        </div>
      </div>
    )}
    </SuggestionsProvider>
  );
};

export default CaseDetail;